/* global window */
import React, { useCallback, useEffect, useState, useRef, useContext, forwardRef, useImperativeHandle, startTransition } from 'react';
import '../styles/SearchBox.css';
import SearchResultsGroup from './SearchResultsGroup';
import SearchResultsItemCollection from './SearchResultsItemCollection';
import SearchResultsItemProduct from './SearchResultsItemProduct';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useIsRendered from '../hooks/useIsRendered';
import { LoadingContext } from './context/LoadingContext';
import getPricesFromQuery from '../helpers/getPricesFromQuery';
import Stack from 'react-bootstrap/Stack';
import PriceFilterSuggestion from './PriceFilterSuggestion';
import constants from '../constants.json' assert { type: 'json' };
import { FaSearch } from 'react-icons/fa';
import useDynamicPath from '../hooks/useDynamicPath';
import AppContext from './context/AppContext';
import { ToastContext } from './context/ToastContext';

const DEFAULT_NON_PAGE_LIMIT = 4;
const TIMEOUT_DURATION = 600;

// eslint-disable-next-line
function SearchBox({ isMobile, isPage = false, productList = [], setProductList = () => { }, searchLoaded = false, setSearchLoaded = () => { }, setSearchLoading = () => { }, searchQuery = {}, setSearchQuery = () => { }, setPagesData = () => { }, setExpanded = () => { } }, searchBoxRef) {
    const { sendMessage } = useContext(ToastContext);
    const isRendered = useIsRendered();
    const navigate = useNavigate();
    const searchBox = useRef(null);
    const [collections, setCollections] = useState([]);
    const [searchExpanded, setSearchExpanded] = useState(false);
    const [collectionList, setCollectionList] = useState([]);
    const [prices, setPrices] = useState({
        minPrice: null,
        maxPrice: null,
    });
    const [searchParams, setParams] = useSearchParams();
    const [alterPath, setAlterPath] = useState(false);
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const { appContext } = useContext(AppContext);
    const appConfig = appContext.config.themeConfig;

    useImperativeHandle(searchBoxRef, () => ({
        enableAlterPath() {
            setAlterPath(true);
        }
    }), []);

    // Loading state
    const {
        startLoading,
        finishLoading,
    } = useContext(LoadingContext);
    const handleSearch = useCallback(async (isFirstRender = false) => {
        try {
            if (searchQuery.q?.length < 3 && searchQuery.v?.length < 3) {
                return;
            }
            setSearchLoading(true);
            startLoading();
            const commonParams = new URLSearchParams(Object.assign({}, ...Object.entries(searchQuery).filter(([_k, v]) => !Array.isArray(v) ? Boolean(v) : v.length > 0).map(([k, v]) => ({ [k]: v })), {
                ...!isPage === true && ({ limit: DEFAULT_NON_PAGE_LIMIT })
            })).toString();
            const productParams = commonParams + `&fields=${constants.MINIMAL_PRODUCT_FIELDS.join(',')}`;
            const collectionParams = commonParams + `&fields=${constants.MINIMAL_COLLECTION_FIELDS.join(',')}`;
            // Fetch data
            const [collectionResponse, productResponse] = await Promise.all([
                fetch(new URL('api/collections?' + collectionParams, process.env.APP_URL)),
                fetch(new URL('api/products/filter?' + productParams, process.env.APP_URL)),
            ]);
            // Collections
            const collectionData = await collectionResponse.json();
            setCollectionList(collectionData.collections ?? []);
            // Products
            const productData = await productResponse.json();
            setProductList(productData.products ?? []);
            if (productData.pageInfo) {
                setPagesData({
                    limit: productData.pageInfo.limitPerPage,
                    count: productData.pageInfo.totalObjects,
                    pages: productData.pageInfo.totalPages,
                    availableTagPrefixes: productData.pageInfo.availableTagPrefixes ?? [],
                    availableTagFilters: productData.pageInfo.availableTagFilters ?? [],
                    availableVendors: productData.pageInfo.availableVendors ?? [],
                    productMaxPrice: productData.pageInfo.productMaxPrice,
                });
            }
            // Update Search
            setSearchLoaded(true);
            setSearchLoading(false);
            finishLoading();
            if (!isFirstRender && !isPage) {
                setSearchExpanded(true);
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
            setSearchLoading(false);
            setSearchLoaded(false);
            finishLoading();
        }
    }, [searchQuery, isPage]);
    useEffect(() => {
        let autoSearchTimer = null;
        const AUTO_SEARCH_WAIT_MS = isPage ? 400 : 600;
        autoSearchTimer = setTimeout(() => handleSearch(!isRendered), AUTO_SEARCH_WAIT_MS);
        if (!searchQuery.q) {
            startTransition(() => {
                setSearchExpanded(false);
            });
        }
        return () => {
            clearTimeout(autoSearchTimer);
        };
    }, [handleSearch]);
    useEffect(() => {
        // Detect outside clicks
        const detectOutsideClick = (e) => {
            if (searchBox.current
                && !searchBox.current.contains(e.target)
                && searchExpanded === true) {
                setSearchExpanded(false);
            }
        };
        window.addEventListener('click', detectOutsideClick, true);
        return () => {
            window.removeEventListener('click', detectOutsideClick, true);
        };
    }, [searchExpanded]);
    const handleSearchButtonPress = () => {
        if (!searchQuery.q) {
            return;
        }

        if (isMobile) {
            setExpanded(false);
        }

        if (!isPage) {
            return navigate(genResultsUrl());
        }
        return handleSearch();
    };
    const genResultsUrl = () => (`/search.php?${new URLSearchParams(Object.assign({}, ...Object.entries(searchQuery).filter(([_k, v]) => !Array.isArray(v) ? Boolean(v) : v.length > 0).map(([k, v]) => ({ [k]: v })), { page: 0 })).toString()}`);
    const handleSearchKeyPress = (e) => {
        if (!searchQuery.q) {
            return;
        }

        if (e.key === 'Enter') {
            if (isMobile) {
                setExpanded(false);
            }

            if (!isPage) {
                return navigate(genResultsUrl());
            }
            return handleSearch();
        }
    };

    const setPricesWrapper = updates => {
        setPrices(prevPrices => ({
            ...prevPrices,
            ...updates,
        }));
    };

    const onQueryChange = e => {
        setSearchQuery({
            ...searchQuery,
            q: e.target.value,
        });
    };

    const onPriceSuggestionAccept = (price) => {
        let priceObject = prices[price];

        if (!priceObject) {
            priceObject = {};
        }

        setSearchQuery({
            ...searchQuery,
            q: prices[price].newQuery,
            [price]: priceObject.value,
        });

        setPricesWrapper({ [price]: null });

        if (alterPath) {
            const currentParams = Object.fromEntries(searchParams.entries());
            const updatedParams = {
                ...currentParams,
                [price]: priceObject.value
            };

            setParams(updatedParams);
        }
    };

    // Set minPrice, maxPrice from search query
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const { minPrice: oldMinPrice, maxPrice: oldMaxPrice } = searchQuery;
            const { minPrice, maxPrice } = getPricesFromQuery(searchQuery.q);

            if (minPrice && oldMaxPrice && parseFloat(oldMaxPrice) > parseFloat(minPrice.value)) {
                setPricesWrapper({ minPrice });
            } else {
                setPricesWrapper({ minPrice: null });
            }

            if (maxPrice && oldMinPrice && parseFloat(oldMinPrice) < parseFloat(maxPrice.value)) {
                setPricesWrapper({ maxPrice });
            } else {
                setPricesWrapper({ maxPrice: null });
            }

            if (!oldMaxPrice && minPrice) {
                setPricesWrapper({ minPrice });
            }

            if (!oldMinPrice && maxPrice) {
                setPricesWrapper({ maxPrice });
            }
        }, TIMEOUT_DURATION);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const { minPrice, maxPrice } = prices;

    const fetchCollections = useCallback(async () => {
        try {
            const url = new URL('api/nav', process.env.APP_URL);
            url.searchParams.append('collectionUrlPrefix', collectionPath[0].pathBeforeParams);
            url.searchParams.append('imagePlacement', appConfig.imagePlacements?.collection?.menuImage);
            url.searchParams.append('depth', 1);
            const response = await fetch(url);
            const data = await response.json();
            startTransition(() => setCollections(data?.navData ?? []));
        } catch (e) {
            sendMessage(e.message, 'danger');
        }
    }, []);

    useEffect(() => {
        fetchCollections();
    }, []);

    return (<div
        ref={searchBox}
        className={'search-box'}>
        <div
            className={'d-flex'}
            style={{
                flexGrow: '1',
            }}>
            <select
                className={'search-box--category-selector'}
                style={{
                    background: 'var(--ab-bg-color-dark)',
                    border: 'solid 1px #cccccc',
                    maxWidth: '130px',
                }}
                value={searchQuery.c}
                onChange={(e) => {
                    setSearchQuery({
                        ...searchQuery,
                        c: e.target.value,
                    });
                }}>
                <option
                    value={''}>
                    All Categories
                </option>
                {collections.map((category) => (<option
                    key={category.id}
                    value={category.id}>
                    {category.title}
                </option>))}
            </select>
            <input
                type={'text'}
                placeholder={'Enter Keyword or Part Number...'}
                value={searchQuery.q}
                onChange={onQueryChange}
                onFocus={() => setSearchExpanded(true)}
                onKeyDown={handleSearchKeyPress} />
        </div>
        <button
            type={'submit'}
            style={{
                background: 'var(--primary-color)',
            }}
            aria-label={'Search'}
            onClick={handleSearchButtonPress}>
            <span
                className={'desktop-only'}>
                Search
            </span>
            <FaSearch
                className={'mobile-only'} />
        </button>
        {(searchLoaded && !isPage) && (<div
            className={'search--results'}
            style={{ ...searchExpanded === true ? ({ display: 'flex' }) : ({ display: 'none' }) }}>
            <Stack
                direction={'horizontal'}
                className={'py-3 px-2'}
                gap={1}
            >
                {minPrice ? (
                    <PriceFilterSuggestion
                        onClick={() => onPriceSuggestionAccept('minPrice')}
                        matchedString={minPrice.matchedString}
                    />
                ) : null}
                {maxPrice ? (
                    <PriceFilterSuggestion
                        onClick={() => onPriceSuggestionAccept('maxPrice')}
                        matchedString={maxPrice.matchedString}
                    />
                ) : null}
            </Stack>
            {!collectionList.length && !productList.length && (<p
                style={{
                    padding: '5px 10px',
                    marginTop: '1em'
                }}>No results found.</p>)}
            {collectionList.length > 0 && (<SearchResultsGroup
                title={'Categories'}>
                {collectionList.map((collection) => (<SearchResultsItemCollection
                    onClick={() => setSearchExpanded(false)}
                    key={collection.id}
                    title={collection.title}
                    query={searchQuery.q}
                    minPrice={searchQuery.minPrice}
                    maxPrice={searchQuery.maxPrice}
                    handle={collection.handle} />))}
            </SearchResultsGroup>)}
            {productList.length > 0 && (<SearchResultsGroup
                title={'Products'}>
                {productList
                    .sort((a, b) => {
                        if (a.imageUrl && !b.imageUrl) {
                            return -1;
                        }
                        if (a.imageUrl && b.imageUrl) {
                            return a.id - b.id;
                        }
                        if (!a.imageUrl && b.imageUrl) {
                            return 1;
                        }
                    })
                    .map((product) => (<SearchResultsItemProduct
                        onClick={() => setSearchExpanded(false)}
                        key={product.id}
                        title={product.title}
                        handle={product.handle}
                        query={searchQuery.q}
                        imageUrl={product.imageUrl} />))}
            </SearchResultsGroup>)}
        </div>)}
    </div>);
}

// SearchBox.propTypes = {
//     isMobile: bool,
//     isPage: bool,
//     productList: array,
//     setProductList: func,
//     searchLoaded: bool,
//     setSearchLoaded: func,
//     searchLoading: bool,
//     setSearchLoading: func,
//     searchQuery: object,
//     setSearchQuery: func,
//     setPagesData: func,
//     setExpanded: func,
// };

export default forwardRef(SearchBox);